import { useLanguage } from "../context/LanguageContext";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";

export const TestimonialsSection = () => {
  const { t } = useLanguage();
  const testimonials = t("testimonials.items");

  return (
    <section
      className="
        section-padding
        bg-white
        dark:bg-[#141617]
        transition-colors duration-300
      "
      data-testid="testimonials-section"
    >
      <div className="container-width">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-[#5cb85c] font-semibold text-sm tracking-wider mb-3 block">
            {t("testimonials.sectionTag")}
          </span>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white transition-colors">
            {t("testimonials.title")}
          </h2>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="
                relative
                rounded-2xl p-6 card-hover
                bg-slate-50
                dark:bg-black/30
                border border-transparent
                dark:border-white/10
                transition-colors duration-300
                flex flex-col h-full
              "
              data-testid={`testimonial-${index}`}
            >
              {/* Quote Icon */}
              <div className="absolute top-4 right-4 text-[#5cb85c]/20">
                <Quote size={48} />
              </div>

              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star
                    key={i}
                    size={18}
                    className="fill-amber-400 text-amber-400"
                  />
                ))}
              </div>

              {/* Text */}
              <p className="text-slate-700 dark:text-slate-200 mb-6 relative z-10 leading-relaxed transition-colors flex-1 min-h-0">
                "{testimonial.text}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 mt-auto">
                <div className="w-10 h-10 rounded-full bg-[#5cb85c]/10 flex items-center justify-center">
                  <span className="text-[#5cb85c] font-bold text-sm">
                    {testimonial.name.charAt(0)}
                  </span>
                </div>

                <div>
                  <p className="font-semibold text-slate-900 dark:text-white transition-colors">
                    {testimonial.name}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400 transition-colors">
                    {testimonial.location}
                  </p>
                </div>
              </div>

              {/* subtle edge highlight */}
              <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-black/5 dark:ring-white/5 transition-colors duration-300" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
